import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import {
  cognitoCodeValidator,
  emailValidator,
  passwordConfirmationValidator,
  passwordValidator,
} from 'darwin_core/validators.js';
import {
  resetTriggerFailed,
  SignInAfterResetError,
} from 'darwin_core/password-reset';

import { useAuth } from './AuthenticationContext.jsx';
import { appResetLink, canOpenApp, readResetLink } from './resetLink.js';
import { isTransportError } from './transportError.js';
import BackLink from '../navigation/BackLink.jsx';
import { PROPERTY_PATH } from '../navigation/paths.js';
import HaloLoginDevices from '../components/HaloLoginDevices.jsx';
import LegalFooter from '../legal/LegalFooter.jsx';
import PasswordField from '../components/PasswordField.jsx';
import PasswordRules, {
  PASSWORD_RULES_ATTRIBUTE,
} from '../components/PasswordRules.jsx';
import TextField from '../components/TextField.jsx';

// The messages call the code a PIN code, as the field and the email do, and
// each leads with what to do next: some users are not technical.

// After a refused code. Which one shows depends only on where the email came
// from, never on the account, so it can't tell anyone whether one exists.
const CODE_REFUSED_FROM_LINK =
  'That PIN code didn’t work. Check it matches the one in your email, or get a new one from “Forgot Password?” on the Login screen.';
const CODE_REFUSED_TYPED =
  'That email address and PIN code didn’t work. Check both against your email, or get a new code from “Forgot Password?” on the Login screen.';

// Cognito's answers that mean this code won't reset this account. Which one
// comes back depends on the account: an unknown email gets
// UserNotFoundException (seen on dev), an existing one CodeMismatchException or
// ExpiredCodeException, and one with no verified email, or disabled, or never
// confirmed, InvalidParameterException, NotAuthorizedException or
// UserNotConfirmedException. The user pool doesn't hide that, and can't be
// changed to, so they all get one message, and the form doesn't tell people
// which emails have accounts. That is as far as the page can go: it calls
// Cognito with a public client id, so the API itself still answers each way.
// Too many attempts keeps its own message, because a user needs to know to wait.
const CODE_REFUSED = [
  'CodeMismatchException',
  'ExpiredCodeException',
  'UserNotFoundException',
  'InvalidParameterException',
  'NotAuthorizedException',
  'UserNotConfirmedException',
];

// Cognito also answers InvalidParameterException for a password that starts or
// ends with a space, which would read as a refused code; the app's validator
// allows one, so this page checks for it before sending.
const EDGE_SPACE = 'Remove the space at the start or end of your password.';

// Cognito's refusals in words the user can act on. A raw Cognito message is never
// shown (see SignIn.jsx).
//
// A lost connection is no proof the reset failed: Cognito can set the password
// and the answer still go astray, after which the code is used up and a retry
// is refused. The same goes for a Cognito trigger that fails while finishing
// the reset, such as the one that removes the account's passkeys: Cognito has
// most likely saved the new password by then. So those answers, and a refusal
// after one, say the new password may already work. That depends only on this
// page's own tries, never on the account.
const mayHaveReset = (err) => isTransportError(err) || resetTriggerFailed(err);

function messageForError(err, { emailFromLink, earlierTryLost }) {
  if (isTransportError(err)) {
    return 'Your connection dropped, so your password might already be changed. Try logging in with your new password. If that doesn’t work, come back and try again.';
  }
  if (resetTriggerFailed(err)) {
    return 'Something went wrong while finishing your password reset, so your password might already be changed. Try logging in with your new password. If that doesn’t work, come back and try again.';
  }
  if (CODE_REFUSED.includes(err?.name)) {
    if (earlierTryLost) {
      return 'This PIN code can’t be used again. Your new password was probably saved, so try logging in with it.';
    }
    return emailFromLink ? CODE_REFUSED_FROM_LINK : CODE_REFUSED_TYPED;
  }
  switch (err?.name) {
    case 'InvalidPasswordException':
      return 'Your password needs to tick every rule above.';
    case 'LimitExceededException':
    case 'TooManyRequestsException':
    case 'TooManyFailedAttemptsException':
      return 'Too many tries for now. Please wait a while, then try again.';
    default:
      return 'We couldn’t reset your password. Please try again later.';
  }
}

// The field each value is entered in, for moving focus to the first one in error.
const FIELD_IDS = {
  email: 'reset-email',
  code: 'reset-code',
  password: 'reset-password',
  confirmPassword: 'reset-confirm-password',
};

// The new-password step of a password reset, at /password-code. The reset email's
// link opens it with the email and code; "Already have a reset code?" on Forgot
// password opens it with the email, and the code is typed in. Public, outside the
// auth gate.
//
// Copy, fields and validation follow the app's reset, which asks for the code on
// a screen of its own (app/src/screens/Auth/ForgotPasswordCodeEntry.js) and then
// the new password (ForgotPasswordNewPassword.js): the "New Password" heading,
// the email (read-only when the link brought it), the code asked for only when
// the link didn't bring one, the password rules, Confirm New Password, and
// SUBMIT. Here they share one form. Errors render inline rather than in the
// app's alert dialog.
//
// For password managers: the email is in the form as the username, both password
// fields are new-password with the app's rules for Safari's generator, the code is
// a one-time code, and on success the passwords are left filled while the form
// goes away, so the manager can offer to update the saved login.
//
// On a phone, "Open in the Halo app" hands the same reset to the app. It is a tap,
// not a redirect on load: a desktop browser can't open the app, and a phone may
// not have it.
export default function ForgotPasswordCodeEntry() {
  const { authenticated, currentAuthUser, forgotPasswordSubmit } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const headingRef = useRef(null);
  const submitRef = useRef(null);

  // What the page was opened with: the email's link (its query), or the history
  // entry's state, from "Already have a reset code?" or written below.
  const [given] = useState(() =>
    location.search
      ? { ...readResetLink(location.search), fromLink: true }
      : {
          email: location.state?.email ?? '',
          code: location.state?.code ?? '',
          fromLink: location.state?.fromLink === true,
          codeRefused: location.state?.codeRefused === true,
        },
  );
  // An email the link brought is the account being reset, so it is read-only. One
  // typed on Forgot password can still be corrected here: the browser's Back
  // button goes to Login, not back to that screen.
  const emailFromLink = given.fromLink && given.email !== '';
  // The code is asked for when the page didn't bring a whole one, and once
  // Cognito has refused the one it brought, so it can be checked against the
  // reset email and corrected.
  const [askCode, setAskCode] = useState(
    () => given.codeRefused || !/^\d{6}$/.test(given.code),
  );

  const [email, setEmail] = useState(given.email);
  const [code, setCode] = useState(given.code);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  // Set once the password is changed without signing in with it: because
  // signing in failed, or because another account stays signed in here (its
  // address is `keptAccount`).
  const [passwordSet, setPasswordSet] = useState(null);
  // An earlier submit lost its connection, so the password may already be set.
  const [earlierTryLost, setEarlierTryLost] = useState(false);

  // Take the code out of the address bar, so copying the address or bookmarking
  // the page doesn't pass it on. The history entry's state keeps it for a reload.
  useEffect(() => {
    if (location.search) {
      navigate(location.pathname, { replace: true, state: given });
    }
  }, [location.search, location.pathname, navigate, given]);

  // Move focus to the heading when the page is reached from another screen
  // ("Already have a reset code?"), so a screen reader announces it, and when the
  // view changes to say the password is set. Not when the page is loaded, from
  // the email's link or by a reload: the browser starts at the top anyway, and a
  // heading focused before any interaction shows the keyboard focus ring (as
  // SignIn avoids on first render).
  const [cameFromScreen] = useState(() => navigationType === 'PUSH');
  useEffect(() => {
    if (cameFromScreen || passwordSet) {
      headingRef.current?.focus();
    }
  }, [cameFromScreen, passwordSet]);

  const checks = {
    ...(!emailFromLink && { email: () => emailValidator(email.trim()) }),
    ...(askCode && { code: () => cognitoCodeValidator(code) }),
    password: async () => {
      const result = await passwordValidator(password);
      return result.valid && password.trim() !== password
        ? { valid: false, error: EDGE_SPACE }
        : result;
    },
    confirmPassword: () =>
      passwordConfirmationValidator(password, confirmPassword),
  };

  // As in the app, a password is checked on leaving its field, and everything on
  // submit.
  const checkOnBlur = (field) => async () => {
    const { error: message } = await checks[field]();
    setFieldErrors((errors) => ({ ...errors, [field]: message }));
  };

  const signedInAs = authenticated ? currentAuthUser?.email : undefined;
  const resetFor = email.trim().toLowerCase();
  const otherAccount =
    signedInAs && resetFor && signedInAs.toLowerCase() !== resetFor
      ? signedInAs
      : null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    const fields = Object.keys(checks);
    const results = await Promise.all(fields.map((field) => checks[field]()));
    const errors = Object.fromEntries(
      fields.map((field, i) => [field, results[i].error]),
    );
    // Drawn before focus moves, so the field already has its error when a screen
    // reader announces it.
    flushSync(() => setFieldErrors(errors));
    const firstInvalid = fields.find((field) => errors[field]);
    if (firstInvalid) {
      document.getElementById(FIELD_IDS[firstInvalid])?.focus();
      return;
    }

    setPending(true);
    try {
      const result = await forgotPasswordSubmit(email.trim(), code, password);
      if ('keptAccount' in result) {
        // Set, and another account stays signed in (see forgotPasswordSubmit).
        // The code is used up: drop it from the history entry, so a reload
        // doesn't bring back a form that can only fail.
        navigate(location.pathname, { replace: true, state: null });
        setPending(false);
        setPasswordSet({
          resetFor: email.trim(),
          keptAccount: result.keptAccount,
        });
        return;
      }
      navigate(PROPERTY_PATH, { replace: true });
    } catch (err) {
      if (err instanceof SignInAfterResetError) {
        // As above, the code is used up.
        navigate(location.pathname, { replace: true, state: null });
        setPending(false);
        setPasswordSet({ resetFor: email.trim() });
        return;
      }
      const refused = CODE_REFUSED.includes(err?.name);
      if (refused) {
        // Kept in the history entry too, so a reload or Back still asks for the
        // code instead of resending the refused one.
        navigate(location.pathname, {
          replace: true,
          state: { ...given, email: email.trim(), code, codeRefused: true },
        });
      }
      // Drawn first, so focus can go back to what needs fixing: disabling the
      // form for the request took it away.
      flushSync(() => {
        setPending(false);
        setAskCode((asked) => asked || refused);
        setError(messageForError(err, { emailFromLink, earlierTryLost }));
        if (mayHaveReset(err)) {
          setEarlierTryLost(true);
        }
      });
      if (refused) {
        document.getElementById(FIELD_IDS.code)?.focus();
      } else if (err?.name === 'InvalidPasswordException') {
        document.getElementById(FIELD_IDS.password)?.focus();
      } else {
        submitRef.current?.focus();
      }
    }
  };

  const nav = (
    <div className="auth-screen__nav">
      <BackLink to={PROPERTY_PATH} label={authenticated ? 'Back' : 'Login'} />
    </div>
  );

  if (passwordSet) {
    return (
      <main className="app-shell auth-screen">
        {nav}
        <div className="auth-brand">
          <HaloLoginDevices />
        </div>
        <h1
          id="main-content"
          className="auth-screen__heading"
          ref={headingRef}
          tabIndex={-1}
        >
          Password changed
        </h1>
        <p className="auth-screen__intro">
          {passwordSet.keptAccount ? (
            <>
              The password for <strong>{passwordSet.resetFor}</strong> has been
              changed. You’re still logged in as{' '}
              <strong>{passwordSet.keptAccount}</strong>. To use{' '}
              {passwordSet.resetFor}, log out, then log in with the new
              password.
            </>
          ) : (
            'Your password has been changed. Log in with your new password.'
          )}
        </p>
        <LegalFooter />
      </main>
    );
  }

  // The app's intro for the email-but-no-code case ("If a matching account
  // exists on our system then an email will have been sent to …") is shortened
  // here for less technical users. It makes no claim about the account either.
  let intro;
  if (!askCode) {
    intro = 'Please enter a new password';
  } else if (given.email) {
    intro =
      'Enter the PIN code from your password reset email, and choose a new password.';
  } else {
    intro =
      'Please enter your email, the PIN code from your password reset email, and a new password.';
  }

  return (
    <main className="app-shell auth-screen">
      {nav}
      <div className="auth-brand">
        <HaloLoginDevices />
      </div>
      <h1
        id="main-content"
        className="auth-screen__heading"
        ref={headingRef}
        tabIndex={-1}
      >
        New Password
      </h1>
      <p className="auth-screen__intro">{intro}</p>
      {/* A link can be opened in a browser signed in to another account. The
          reset leaves that account signed in (see forgotPasswordSubmit); say so
          before it happens, since someone may expect to be logged in to the
          account they reset. */}
      {otherAccount ? (
        <p className="auth-screen__intro">
          You’re logged in as <strong>{otherAccount}</strong>. Changing this
          password won’t log you out.
        </p>
      ) : null}
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        {/* The account, as the username, so a password manager knows which
            saved login the new password belongs to. When the link brought it,
            it is read-only, never disabled: browsers leave a disabled field out
            of the form's data. */}
        <TextField
          id={FIELD_IDS.email}
          name="email"
          type="email"
          label="Email"
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          readOnly={emailFromLink}
          disabled={pending && !emailFromLink}
          required
          error={fieldErrors.email}
        />
        {askCode ? (
          <TextField
            id={FIELD_IDS.code}
            name="code"
            label="Pin Code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            // Digits only, up to six. No maxLength: it would cut a pasted
            // " 123456" or "123 456" to six characters before the spaces are
            // taken out, losing a digit. A digit typed into a full code is
            // ignored, as maxLength would, rather than pushing the last one off.
            onChange={(event) => {
              const digits = event.target.value.replace(/\D+/g, '');
              setCode((current) =>
                digits.length > 6 && current.length === 6
                  ? current
                  : digits.slice(0, 6),
              );
            }}
            // A paste with a whole code in it replaces whatever the field
            // holds, such as a refused code: its first six digits, so a line
            // of the email ("Your code is 123456, valid for 1 hour") works.
            // A shorter paste goes in as typing does.
            onPaste={(event) => {
              const digits = event.clipboardData
                .getData('text')
                .replace(/\D+/g, '');
              if (digits.length >= 6) {
                event.preventDefault();
                setCode(digits.slice(0, 6));
              }
            }}
            disabled={pending}
            required
            error={fieldErrors.code}
          />
        ) : null}
        <PasswordField
          id={FIELD_IDS.password}
          name="new-password"
          label="Password"
          className="text-field--tight"
          autoComplete="new-password"
          passwordrules={PASSWORD_RULES_ATTRIBUTE}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          onBlur={checkOnBlur('password')}
          disabled={pending}
          required
          error={fieldErrors.password}
        />
        <PasswordRules password={password} />
        <PasswordField
          id={FIELD_IDS.confirmPassword}
          name="confirm-password"
          label="Confirm New Password"
          autoComplete="new-password"
          passwordrules={PASSWORD_RULES_ATTRIBUTE}
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          onBlur={checkOnBlur('confirmPassword')}
          disabled={pending}
          required
          error={fieldErrors.confirmPassword}
        />
        {/* Always present so assistive tech has the live region before an error
            is set; only Cognito's answer, or no answer, populates it. */}
        <p
          id="reset-error"
          className="form-error"
          role="alert"
          aria-live="assertive"
        >
          {error}
        </p>
        <button
          type="submit"
          className="auth-form__submit"
          ref={submitRef}
          disabled={pending}
        >
          {pending ? 'Submitting…' : 'Submit'}
        </button>
      </form>
      {/* For the email's link opened on a phone. Only a whole code the link
          brought goes to the app: given a code, the app goes straight on to its
          new password screen, where one partly typed here, or one Cognito has
          refused, would only be refused again. Without one, the app asks for it
          first. */}
      {emailFromLink && canOpenApp() ? (
        <a
          className="link-button auth-screen__app-link"
          href={appResetLink(email, askCode ? '' : given.code)}
        >
          Open in the Halo app
        </a>
      ) : null}
      <LegalFooter />
    </main>
  );
}
